import { addDecoratorToText } from "@/components/expression/utils/decorators/utils";
import { DEFAULT_CURSOR_CONTEXT_ID } from "@/models/constants";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { SymbolNodeModel } from "@/models/nodes/SymbolNodeModel";

import type { NodeModel } from "@/models/nodes/types";
import type { NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";

export class TextVisitor implements NodeVisitor<string> {
  private showSelectable = false;

  private showCursor = false;

  private showEditableCursor = false;

  private showUndefinedGroupSymbols = false;

  private showEditable = false;

  private cursorContextId: string;

  constructor({
    showSelectable = false,
    showCursor = false,
    showUndefinedGroupSymbols = false,
    showEditable = false,
    showEditableCursor = false,
    cursorContextId = DEFAULT_CURSOR_CONTEXT_ID,
  }: {
    /** Show selected nodes as {node}, used mostly for tests  */
    showSelectable?: boolean;
    /** Show cursor as x+y|, used mostly for tests  */
    showCursor?: boolean;
    /** Show undefined groups opening and closing symbols */
    showUndefinedGroupSymbols?: boolean;
    /** Show currently editing node */
    showEditable?: boolean;
    showEditableCursor?: boolean;
    cursorContextId?: string;
  } = {}) {
    this.showSelectable = showSelectable;
    this.showCursor = showCursor;
    this.showUndefinedGroupSymbols = showUndefinedGroupSymbols;
    this.showEditable = showEditable;
    this.showEditableCursor = showEditableCursor;
    this.cursorContextId = cursorContextId;
  }

  private cursorableText(node: NodeModel, str: string, index?: number) {
    if (this.showCursor) {
      const { cursor } = node.doc.cursorContext(this.cursorContextId) ?? {};

      if (cursor) {
        return `${str}${cursor.isCurrent(node, index) ? "|" : ""}`;
      }
    }

    return str;
  }

  private selectableText(node: NodeModel, str: string, index?: number) {
    if (this.showSelectable) {
      const { selection } = node.doc.cursorContext(this.cursorContextId) ?? {};

      if (selection) {
        return node.doc.selection.isSelected(index == null ? node : node.nodes[index])
          ? `{${str}}`
          : str;
      }
    }

    return str;
  }

  private decorateText(node: NodeModel, text: string) {
    let result = text;
    result = this.selectableText(node, result);
    result = this.cursorableText(node, result);
    result = this.editableText(node, result);
    return result;
  }

  private editableText(node: NodeModel, str: string) {
    if (this.showEditable && node.doc.input.isEditing(node)) {
      let text = str;
      if (this.showEditableCursor) {
        const selection = node.doc.input.inputSelection;
        if (selection) {
          if (selection.from !== selection.to) {
            text = `${text.substring(0, selection.from)}⸤${text.substring(selection.from, selection.to)}⸥${text.substring(selection.to)}`;
          } else {
            text = `${text.substring(0, selection.from)}|${text.substring(selection.from)}`;
          }
        }
      }
      return `~${text}~`;
    }
    return str;
  }

  visitGroupNode(node: NodeModel<"group">): string {
    const {
      openingSymbol,
      closingSymbol,
      casesSystemEnabled,
      doc: { cursor },
    } = node;
    let text = `${openingSymbol || (this.showUndefinedGroupSymbols ? "⸤" : "")}${
      cursor.isCurrent(node, 0) && this.showCursor ? "|" : ""
    }${(node.inlineNode || node.matrixNode)?.accept(this)}${
      closingSymbol || (!casesSystemEnabled && this.showUndefinedGroupSymbols ? "⸥" : "")
    }`;
    text = addDecoratorToText(text, node.decorators[0]?.name);
    text = this.selectableText(node, text);
    text = this.cursorableText(node, text);

    return text;
  }

  visitOperatorNode(node: NodeModel<"operator">): string {
    if (node.placement === "prefix") {
      let symbolText = node.operatorSymbol;
      symbolText = this.selectableText(node, symbolText, 0);
      symbolText = this.cursorableText(node, symbolText, 0);
      return `${symbolText}${node.parameters[0]?.accept(this)}`;
    }

    if (node.placement === "function") {
      let text = node.doc.cursor.isCurrent(node, 0) ? "|" : "";
      const bodyText: string[] = [];
      for (const param of node.parameters) {
        bodyText.push(param.accept(this));
      }
      text += `${node.operatorSymbol}(${bodyText.join(",")})`;

      text = this.selectableText(node, text);
      text = this.cursorableText(node, text);
      text = this.editableText(node, text);

      return text;
    }

    if (node.placement === "postfix") {
      let text = `${node.parameters[0]?.accept(this)}${node.operatorSymbol}`;
      text = this.selectableText(node, text);
      text = this.cursorableText(node, text);
      return text;
    }

    if (node.arity === "unary") {
      return `${node.operatorSymbol}${node.parameters[0]?.accept(this)}`;
    }

    let text = this.selectableText(
      node,
      node.parameters
        .map((n, i) => {
          let symbolText =
            // !(n.is(OperatorNodeModel) && n.placement === "prefix") &&
            i !== 0 ? node.operatorSymbol : "";
          symbolText = this.selectableText(node, symbolText, i);
          symbolText = this.cursorableText(node, symbolText, i);

          return `${symbolText}${n.accept(this)}`;
        })
        .join(""),
    );
    text = this.cursorableText(node, text);
    return text;
  }

  visitOperatorSymbolNode(node: NodeModel<"operatorSymbol">): string {
    return this.selectableText(node, node.value);
  }

  visitLabelNode(node: NodeModel<"label">): string {
    return this.cursorableText(
      node,
      [node.inlineNode]
        .map((n, i) => {
          if (n) {
            let symbolText = i === 0 ? node.labelSymbol : "";
            symbolText = this.selectableText(node, symbolText, i);
            symbolText = this.cursorableText(node, symbolText, i);

            return `${symbolText}${n.accept(this)}`;
          }
          return "";
        })
        .join(""),
    );
  }

  visitSymbolNode(node: NodeModel<"symbol">): string {
    /**
     * For now we pass just one decorator.
     * Need to figure out how to properly add multiple decorators to string
     */
    let text = addDecoratorToText(node.value, node.decorators[0]?.name);

    if (node.labelNodes.length > 0) {
      const labels = node.labelNodes.map((n) => n.accept(this)).join("");
      text = `(${text}${labels})`;
    }

    text = this.selectableText(node, text);
    text = this.cursorableText(node, text);
    text = this.editableText(node, text);
    return text;
  }

  visitNumberNode(node: NodeModel<"number">): string {
    let text = this.decorateText(node, node.value);

    if (node.power) {
      text = `${text}${node.power.accept(this)}`;
    }

    if (node.subscript) {
      text = `${text}${node.subscript.accept(this)}`;
    }

    if (node.unitNode?.placement === "postfix") {
      const hasSubSup = !!(node.power || node.subscript);

      text = `${text}${hasSubSup ? "[" : ""}${node.unitNode.accept(this)}${hasSubSup ? "]" : ""}`;
    } else if (node.unitNode?.placement === "prefix") {
      text = `${node.unitNode.accept(this)}${text}`;
    }

    return text;
  }

  visitVariableNode(node: NodeModel<"variable">): string {
    let text = node.symbolNode?.accept(this) ?? "";

    if (node.power) {
      text = `${text}${node.power.accept(this)}`;
    }

    const bodyText = node.parameters.map((n) => n.accept(this)).join(",");

    text =
      bodyText || node.hasParams
        ? `${text}${node.openingSymbol}${bodyText}${node.closingSymbol}`
        : text;
    text = this.decorateText(node, text);

    if (node.unitNode?.placement === "postfix") {
      text = `${text}${node.unitNode.accept(this)}`;
    } else if (node.unitNode?.placement === "prefix") {
      text = `${node.unitNode.accept(this)}${text}`;
    }

    return text;
  }

  visitTextNode(node: NodeModel<"text">): string {
    return node.nodes
      .map((n) => {
        return n.accept(this);
      })
      .join("\n");
  }

  visitWrapperNode(node: NodeModel<"wrapper">): string {
    return node.inlineNode ? node.inlineNode.accept(this) : "";
  }

  visitConstantNode(node: NodeModel<"constant">): string {
    let text = "";
    if (node.nodes[0]?.is(SymbolNodeModel)) {
      text = node.nodes[0].accept(this);
    }
    text = this.decorateText(node, text);

    if (node.unitNode?.placement === "postfix") {
      text = `${text}${node.unitNode.accept(this)}`;
    } else if (node.unitNode?.placement === "prefix") {
      text = `${node.unitNode.accept(this)}${text}`;
    }

    return text;
  }

  visitFunctionNode(node: NodeModel<"function">): string {
    let text = node.symbolNode?.accept(this) ?? "";

    if (node.power) {
      text = `${text}${node.power.accept(this)}`;
    }

    const bodyText = node.parameters.map((n) => n.accept(this)).join(",");

    text = `${text}${node.openingSymbol}${bodyText}${node.closingSymbol}`;
    text = this.selectableText(node, text);
    text = this.cursorableText(node, text);
    text = this.editableText(node, text);

    return text;
  }

  visitInlineNode(node: NodeModel<"inline">): string {
    const wrapInParens =
      node.nodes.length > 1 &&
      node.parent?.is(OperatorNodeModel) &&
      node.parent.placement !== "function";
    return `${wrapInParens ? "(" : ""}${this.cursorableText(node, "", 0)}${node.nodes
      .map((n, i) => {
        let bodyText = "";

        if (
          node.nodes[i + 1] &&
          !node.nodes[i + 1].is(OperatorSymbolNodeModel) &&
          !node.nodes[i + 1].is(OperatorNodeModel) &&
          !n.is(OperatorSymbolNodeModel)
        ) {
          bodyText = "*";
        }

        return `${this.cursorableText(node, n.accept(this), i + 1)}${bodyText}`;
      })
      .join("")}${wrapInParens ? ")" : ""}`;
  }

  visitPlainTextNode(node: NodeModel<"plainText">): string {
    let text = node.value;
    text = this.selectableText(node, text);
    text = this.cursorableText(node, text);
    text = this.editableText(node, text);
    return text;
  }

  visitMatrixNode(node: NodeModel<"matrix">): string {
    const { cursor } = node.doc;
    let text = node.matrix
      .map(
        (row) =>
          `[${row
            .map((col, i) => {
              const augmentedBar = node.augmentedBarIndices.includes(i) ? "¦" : "";
              const cellText = `${col.accept(this)}${augmentedBar}`;

              if (col.is(InlineNodeModel) && col.isEmpty) {
                if (this.showCursor && cursor.isCurrent(col, 0)) {
                  return cellText;
                }

                return `-${cellText}`;
              }

              return cellText;
            })
            .join(",")}]`,
      )
      .join(",");

    text = this.selectableText(node, text);
    text = this.cursorableText(node, text);

    return text;
  }

  visitDecoratorNode(node: NodeModel<"decorator">): string {
    throw new Error(`TextVisitor is not implemented for ${node.type}`);
  }

  visitUnitNode(node: NodeModel<"unit">): string {
    let text = node.inlineNode ? node.inlineNode.accept(this) : "";

    if (this.showCursor) {
      const cursored = node.doc.cursor.isOnNode(node);
      text = cursored ? `[${text}]` : text;
    }

    return text;
  }

  visitGraphNode(node: NodeModel<"graph">): string {
    throw new Error(`TextVisitor is not implemented for ${node.type}`);
  }

  visitGraphExpressionNode(node: NodeModel<"graphExpression">): string {
    return node.inlineNode?.accept(this) ?? "";
  }

  visitPlaceholderNode(node: NodeModel<"placeholder">): string {
    const bodyText = node.inlineNode?.accept(this) ?? "";

    if (this.showCursor) {
      const cursored = node.doc.cursor.isOnNode(node);
      return cursored ? `[_${bodyText}_]` : `[_]`;
    }

    return bodyText ? `[_${bodyText}_]` : `[_]`;
  }

  visitComputedReferenceNode(node: NodeModel<"computedReference">): string {
    return node.nodes.map((n) => n.accept(this)).join("");
  }

  visitComputedResultNode(node: NodeModel<"computedResult">): string {
    return node.nodes.map((n) => n.accept(this)).join("");
  }
}
